import React from "react";
import Modal from "../../../component/ui/modal";
import TextInput from "../../../component/element/forms/TextInput";
import SelectOption from "../../../component/element/forms/SelectOption";
import { ISimpleData } from "../../../store/types/misc";
import { IUser } from "../../../store/types/users";
import useUserForm from "../hooks/useUserForm";
import { get_states } from "../../../services/apis/locations";
import { useAppDispatch } from "../../../store";
import { clearStates } from "../../../store/slices/locations";

type IUserFormModal = {
  open: boolean;
  user?: IUser | null;
  onClose: () => void;
};

const UserFormModal = ({ open, user = null, onClose }: IUserFormModal) => {
  const dispatch = useAppDispatch();
  const {
    values,
    errors,
    countries,
    states,
    loading,
    onChange,
    setFieldValue,
    submit,
  } = useUserForm(open, user);

  const handleSubmit = async () => {
    if (!loading && (await submit())) onClose();
  };
  const fieldStatus = (field: keyof typeof errors) =>
    errors[field] ? "error: this field is required" : "";
  const countryOptions: ISimpleData[] = countries.map((country) => ({
    id: String(country.id),
    name: country.name,
  }));
  const stateOptions: ISimpleData[] = states.map((state) => ({
    id: String(state.id),
    name: state.name,
  }));

  return (
    <Modal
      title={user ? "Edit User" : "Create User"}
      open={open}
      onClose={onClose}
      onOkClick={handleSubmit}
      isLoading={loading}
      variant="medium"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <TextInput
            label="Full name"
            name="name"
            type="text"
            placeholder="Enter full name"
            value={values.name}
            onChange={onChange}
            status={fieldStatus("name")}
            required
          />
          {errors.name && <FieldError>{errors.name}</FieldError>}
        </div>
        <div>
          <TextInput
            label="Email"
            name="email"
            type="email"
            placeholder="name@example.com"
            value={values.email}
            onChange={onChange}
            status={fieldStatus("email")}
            required
          />
          {errors.email && <FieldError>{errors.email}</FieldError>}
        </div>
        <div>
          <TextInput
            label="Username"
            name="username"
            type="text"
            placeholder="Enter username"
            value={values.username}
            onChange={onChange}
            status={fieldStatus("username")}
            required
          />
          {errors.username && <FieldError>{errors.username}</FieldError>}
        </div>
        <div>
          <TextInput
            label={user ? "New password (optional)" : "Password"}
            name="password"
            type="password"
            placeholder={
              user ? "Leave blank to keep current" : "At least 8 characters"
            }
            value={values.password}
            onChange={onChange}
            status={fieldStatus("password")}
            required={!user}
          />
          {errors.password && <FieldError>{errors.password}</FieldError>}
        </div>
        <div>
          <TextInput
            label="Phone number"
            name="phone_number"
            type="tel"
            placeholder="Enter phone number"
            value={values.phone_number}
            onChange={onChange}
            status={fieldStatus("phone_number")}
            required
          />
          {errors.phone_number && (
            <FieldError>{errors.phone_number}</FieldError>
          )}
        </div>
        <div className="flex items-center gap-2 pt-7">
          <input
            id="is_elite"
            type="checkbox"
            checked={values.is_elite}
            onChange={(event) =>
              setFieldValue("is_elite", event.currentTarget.checked)
            }
            className="h-4 w-4 rounded border-gray-300"
          />
          <label htmlFor="is_elite" className="text-sm font-medium">
            Elite user
          </label>
        </div>
        <div>
          <SelectOption
            items={countryOptions}
            label="Country"
            placeholder="Select country"
            value={values.country_id}
            onChange={(countryId) => {
              setFieldValue("country_id", countryId);
              setFieldValue("state_id", "");
              dispatch(clearStates());
              if (countryId) void get_states(dispatch, countryId);
            }}
          />
          {errors.country_id && <FieldError>{errors.country_id}</FieldError>}
        </div>
        <div>
          <SelectOption
            items={stateOptions}
            label="State"
            placeholder="Select state"
            value={values.state_id}
            onChange={(stateId) => setFieldValue("state_id", stateId)}
          />
          {errors.state_id && <FieldError>{errors.state_id}</FieldError>}
        </div>
      </div>
    </Modal>
  );
};

const FieldError = ({ children }: { children: React.ReactNode }) => (
  <p className="mt-1 text-sm text-red-600" role="alert">
    {children}
  </p>
);

export default UserFormModal;
